import React, { useState, useCallback, useEffect } from 'react'

import { useInView } from 'react-cool-inview'

import {
  Textarea,
  Button,
  Spinner,
  Checkbox,
  Icon,
  Like,
  Dislike,
  Chat,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  func, number, oneOf, bool, string, arrayOf, shape,
} from 'prop-types'

import Sidebar from 'Pods/Shared/Sidebar/Sidebar'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import Posts from './Components/Posts/Posts'
import Rating from './Components/Rating/Rating'
import Tabs from './Components/Tabs/Tabs'
import { FILTER_TYPES } from './constants'
import useCommentForm from './hooks/useCommentForm'
import useItemFiltering from './hooks/useItemFiltering'
import useReviewActions from './hooks/useReviewActions'

function ReviewsSidebar({
  isOpen,
  closeSidebar,
  comments = EMPTY_ARRAY,
  ratings = EMPTY_ARRAY,
  initialFilterBy = FILTER_TYPES.COMMENTS,
  onReviewAdd,
  onReviewRemove,
  isLoading = false,
  userName,
  role,
  canReview,
  commentsCount = 0,
  likeCount = 0,
  dislikeCount = 0,
  hasMoreComments,
  hasMoreRatings,
  isLoggedIn = false,
  isCurrentUserAdmin = false,
  productId = null,
  productOwnerUsername = null,
}) {
  const [filterBy, setFilterBy] = useState(initialFilterBy)

  const getInitialActiveTab = (filterValue, userRole) => {
    if (userRole !== 'buyer' && userRole !== 'seller') {
      return 'comment'
    }

    if (filterValue === FILTER_TYPES.POSITIVE || filterValue === FILTER_TYPES.NEGATIVE) {
      return 'review'
    }
    return 'comment'
  }

  const [activeTab, setActiveTab] = useState(getInitialActiveTab(initialFilterBy, role))

  // Sync filterBy state and activeTab when initialFilterBy prop changes
  useEffect(() => {
    if (initialFilterBy) {
      setFilterBy(initialFilterBy)
      setActiveTab(getInitialActiveTab(initialFilterBy, role))
    }
  }, [initialFilterBy, role])

  const {
    displayedItems,
    counts,
    hasMore,
    isLoadingMore,
    error: paginationError,
    loadNextPage,
  } = useItemFiltering(
    comments,
    ratings,
    filterBy,
    { comments: commentsCount, positive: likeCount, negative: dislikeCount },
    hasMoreComments,
    hasMoreRatings
  )

  const hasPermissionToComment = role !== 'guest'
  const hasPermissionToReview = role === 'buyer' || canReview

  const {
    comment,
    error: commentError,
    canSubmit,
    handleCommentChange,
    characterCount,
    maxLength,
    isNegative,
    setIsNegative,
    hideUsername,
    setHideUsername,
    setError,
    setIsSubmitting,
    resetForm,
  } = useCommentForm(activeTab)

  const { handleCommentPost, handleReviewPost } = useReviewActions({
    userName,
    handleAdd: onReviewAdd,
    handleRemove: onReviewRemove,
    setError,
    setIsSubmitting,
    resetForm,
  })

  const handleSubmit = useCallback(() => {
    if (activeTab === 'comment') {
      handleCommentPost(comment, isNegative, hideUsername)
    } else {
      handleReviewPost(comment, isNegative, hideUsername)
    }
  }, [activeTab, comment, isNegative, hideUsername, handleCommentPost, handleReviewPost])

  const handleFilterChange = useCallback((filter) => {
    if (filter === filterBy) {
      return
    }

    setFilterBy(filter)
  }, [filterBy])

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab)
  }, [])

  const getFloatingIcon = useCallback(() => {
    if (activeTab === 'comment') {
      return Chat
    }

    if (isNegative) {
      return Dislike
    }

    return Like
  }, [activeTab, isNegative])

  const getCurrentTabPermission = useCallback(() => {
    return activeTab === 'comment' ? hasPermissionToComment : hasPermissionToReview
  }, [activeTab, hasPermissionToComment, hasPermissionToReview])

  const getDisplayUsername = useCallback(() => {
    if (activeTab === 'review' && hideUsername) {
      return 'Anonymous Buyer'
    }

    return userName
  }, [activeTab, hideUsername])

  const getPlaceholder = useCallback(() => {
    if (activeTab === 'comment') {
      return 'Feel free to share your thoughts here.'
    }

    if (activeTab === 'review') {
      if (isNegative) {
        return 'Please explain what went wrong or could be improved.'
      }
      return 'Tell us what you liked about this model (optional).'
    }

    return 'Feel free to share your thoughts here.'
  }, [activeTab, isNegative])

  const getIconColor = useCallback(() => {
    if (activeTab === 'review' && isNegative) {
      return 'attention'
    }

    if (activeTab === 'review' && !isNegative) {
      return 'positive'
    }

    return 'dark'
  }, [activeTab, isNegative])

  const getLoadingText = useCallback(() => {
    switch (filterBy) {
      case FILTER_TYPES.POSITIVE:
        return 'Loading positive reviews...'
      case FILTER_TYPES.NEGATIVE:
        return 'Loading negative reviews...'
      default:
        return 'Loading comments...'
    }
  }, [filterBy])

  const handleLogin = useCallback(() => {
    closeSidebar()
    App.events.publish('app:login:open')
  }, [closeSidebar])

  const getPermissionBannerMessage = useCallback(() => {
    if (role === 'guest') {
      return (
        <span>
          You need to{' '}
          <span className="cgt-legacy-link review-sidebar__login-link" onClick={handleLogin}>
            login
          </span>
          {' '}to leave comments
        </span>
      )
    }

    if (activeTab === 'review' && !hasPermissionToReview) {
      return <span>You need to purchase this model to leave reviews</span>
    }

    if (activeTab === 'comment' && !hasPermissionToComment) {
      return <span>You need to purchase this model to leave comments</span>
    }

    return ''
  }, [role, activeTab, hasPermissionToReview, hasPermissionToComment])

  const handleFormSubmit = useCallback((event) => {
    event.preventDefault()
    if (canSubmit) {
      handleSubmit()
    }
  }, [canSubmit, handleSubmit])

  const areThereAnyCommentsOrRatings = displayedItems.length > 0
  const hasAnyItemsAvailable = (comments.length + ratings.length) > 0

  // Infinite scroll detection
  const { observe } = useInView({
    rootMargin: '100px',
    threshold: 0.1,
    onEnter: () => {
      if (hasMore && !isLoadingMore) {
        loadNextPage()
      }
    },
  })

  return (
    <Sidebar
      isOpen={isOpen}
      closeSidebar={closeSidebar}
      title="Reviews & Comments"
      className="review-sidebar review-sidebar--sticky-header"
    >
      <div className="review-sidebar__comments-area">
        <Tabs activeTab={activeTab} onTabChange={handleTabChange} />
        {getCurrentTabPermission() && (
          <form className="review-sidebar__form-container" onSubmit={handleFormSubmit}>
            <div className="review-sidebar__textarea-container">
              <Textarea
                id="comment-review-area"
                placeholder={getPlaceholder()}
                value={comment}
                onChange={handleCommentChange}
                className="review-sidebar__textarea"
                maxLength={maxLength}
                aria-label={`Write a ${activeTab}`}
                aria-describedby={commentError ? 'comment-error' : 'comment-help'}
                aria-invalid={!!commentError}
              />
              {role !== 'guest' && (
                <div className="review-sidebar__floating-user" key={`${activeTab}-${isNegative}-${hideUsername}`}>
                  <Icon
                    key={`icon-${activeTab}-${isNegative}`}
                    svgComponent={getFloatingIcon()}
                    size="small"
                    color={getIconColor()}
                />
                  <span className="review-sidebar__floating-username">
                    {getDisplayUsername()}
                  </span>
                </div>
              )}
            </div>
            <div className="review-sidebar__form-footer">
              <div className="review-sidebar__form-footer-left">
                {activeTab === 'review' && (
                  <div className="review-sidebar__checkboxes">
                    <Checkbox
                      id="negative-checkbox"
                      isChecked={isNegative}
                      onChange={setIsNegative}
                      label="Negative"
                      isControlled
                    />
                    <Checkbox
                      id="hide-username-checkbox"
                      isChecked={hideUsername}
                      onChange={setHideUsername}
                      label="Hide my username"
                      isControlled
                    />
                  </div>
                )}
              </div>
              <div className="review-sidebar__form-footer-right">
                <div id="comment-help" className="review-sidebar__character-count">
                  {characterCount}/{maxLength}
                </div>
                <Button
                  type="primary"
                  actionType="submit"
                  customClass="review-sidebar__post-button"
                  disabled={!canSubmit}
                  aria-label={`Post ${activeTab}`}
                >
                  Post
                </Button>
              </div>
            </div>
            {commentError && (
              <div id="comment-error" className="review-sidebar__error" role="alert">
                {commentError}
              </div>
            )}
          </form>
        )}

        {!getCurrentTabPermission() && (
          <div className="review-sidebar__needs-permission-container">
            {getPermissionBannerMessage()}
          </div>
        )}
        {isLoading && (
          <div className="review-sidebar__loading" aria-live="polite">
            <Spinner size="medium" />
            <span>{getLoadingText()}</span>
          </div>
        )}
        {!isLoading && hasAnyItemsAvailable && (
          <>
            <Posts
              handleFilterChange={handleFilterChange}
              commentsCount={counts.comments}
              likeCount={counts.positive}
              dislikeCount={counts.negative}
              filterBy={filterBy}
            />
            {areThereAnyCommentsOrRatings && (
              <div
                className="review-sidebar__items"
                role="region"
                aria-label={`Filtered reviews: ${displayedItems.length} items`}
                aria-live="polite"
              >
                {displayedItems.map(({
                  id,
                  username: usernameRating,
                  content,
                  updatedAt,
                  score,
                  comment: commentContent,
                  itemType,
                  userId,
                  anonymous,
                  avatar,
                  userRole,
                  replies,
                }) => (
                  <Rating
                    key={`${itemType}_${id}`}
                    id={id}
                    userName={usernameRating}
                    content={content || commentContent}
                    updatedAt={updatedAt}
                    rating={score}
                    userId={userId}
                    anonymous={anonymous}
                    avatar={avatar}
                    userRole={userRole}
                    isLoggedIn={isLoggedIn}
                    ariaLabelPrefix={`${usernameRating} rating`}
                    isCurrentUserAdmin={isCurrentUserAdmin}
                    replies={replies}
                    isInSidebar
                    productId={productId}
                    currentUsername={userName}
                    productOwnerUsername={productOwnerUsername}
                  />
                ))}

                {/* Infinite scroll trigger element */}
                {hasMore && (
                  <div ref={observe} className="review-sidebar__load-trigger" />
                )}

                {isLoadingMore && (
                  <div className="review-sidebar__loading-more" aria-live="polite">
                    <Spinner size="small" />
                    <span className="review-sidebar__loading-more-text">Loading more reviews...</span>
                  </div>
                )}

                {paginationError && (
                  <div className="review-sidebar__pagination-error" role="alert">
                    <span>{paginationError}</span>
                    <Button
                      type="secondary"
                      customClass="review-sidebar__retry-button"
                      onClick={loadNextPage}
                    >
                      Retry
                    </Button>
                  </div>
                )}
              </div>
            )}
            {!areThereAnyCommentsOrRatings && (
              <div className="review-sidebar__media-container">
                <div className="review-sidebar__media-container-empty" role="status">
                  <h3 className="review-sidebar__media-container-empty-title">
                    Nothing found
                  </h3>
                  <p className="review-sidebar__media-container-empty-subtitle">
                    Try selecting a different filter to see available reviews and comments.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
        {!isLoading && !hasAnyItemsAvailable && (
          <div className="review-sidebar__media-container">
            <div className="review-sidebar__media-container-empty" role="status">
              <h3 className="review-sidebar__media-container-empty-title">
                No Reviews or Comments yet
              </h3>
              <p className="review-sidebar__media-container-empty-subtitle">
                Be the first to share your thoughts! Leave a review to help others understand this model&apos;s
                quality and usability.
              </p>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  )
}

ReviewsSidebar.propTypes = {
  closeSidebar: func.isRequired,
  comments: arrayOf(shape({
    id: number,
    userName: string,
    content: string,
    updatedAt: string,
    userRole: oneOf(['seller', 'multiple_buyer', 'buyer', 'community_member']),
  })),
  ratings: arrayOf(shape({
    id: number,
    score: number,
    comment: string,
    updatedAt: string,
    userName: string,
    userRole: oneOf(['seller', 'multiple_buyer', 'buyer', 'community_member']),
  })),
  initialFilterBy: oneOf([FILTER_TYPES.POSITIVE, FILTER_TYPES.NEGATIVE, FILTER_TYPES.COMMENTS]),
  onReviewAdd: func.isRequired,
  onReviewRemove: func.isRequired,
  isLoading: bool,
  userName: string,
  role: oneOf(['guest', 'user', 'buyer', 'seller']).isRequired,
  canReview: bool,
  commentsCount: number,
  likeCount: number,
  dislikeCount: number,
  isOpen: bool.isRequired,
  hasMoreComments: bool.isRequired,
  hasMoreRatings: bool.isRequired,
  isLoggedIn: bool,
  isCurrentUserAdmin: bool,
  productId: number,
  productOwnerUsername: string,
}

export default ReviewsSidebar
